
import React from "react";
import { useReviewsQuery, Review } from "@/hooks/useReviewsQuery";
import { ReviewList } from "@/components/reviews/ReviewList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkle, Star, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";

interface ServiceReviewSectionProps {
  serviceId: number;
}

export const ServiceReviewSection = ({ serviceId }: ServiceReviewSectionProps) => {
  const isMobile = useIsMobile();
  
  const { 
    highlightedReviews, 
    regularReviews, 
    isLoading,
    error,
    refetch
  } = useReviewsQuery(serviceId);

  // Function to render stars for highlighted reviews
  const renderStars = (rating: number) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
            i < rating
              ? "fill-current"
              : "text-gray-300"
          }`}
          style={{ color: i < rating ? 'var(--brand-color-3, #91DA73)' : undefined }}
        />
      ));
  };

  if (isLoading) {
    return (
      <div>
        <h2 className="text-lg font-semibold mb-4">Reviews</h2>
        <div className="space-y-4">
          <Skeleton className="h-20 md:h-24 w-full" />
          <Skeleton className="h-20 md:h-24 w-full" />
          <Skeleton className="h-20 md:h-24 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2 className="text-lg font-semibold mb-4">Reviews</h2>
        <div className="p-4 bg-red-50 text-red-700 rounded-md">
          An error occurred while loading reviews.
          <div className="mt-2">
            <button 
              onClick={() => refetch()}
              className="text-sm underline hover:text-red-800"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">Reviews</h2>
      
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="all">All Reviews</TabsTrigger>
          <TabsTrigger value="highlighted">Highlighted</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all">
          {regularReviews.length === 0 && highlightedReviews.length === 0 ? (
            <div className="text-center p-4 md:p-6 bg-gray-50 rounded-md">
              <p className="text-gray-500">No reviews yet for this service.</p>
            </div>
          ) : (
            <ReviewList reviews={[...highlightedReviews, ...regularReviews]} />
          )}
        </TabsContent>
        
        <TabsContent value="highlighted">
          {highlightedReviews.length === 0 ? (
            <div className="text-center p-4 md:p-6 bg-gray-50 rounded-md">
              <p className="text-gray-500">No highlighted reviews yet.</p>
            </div>
          ) : (
            <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2">
              {highlightedReviews.map((review) => (
                <Card 
                  key={review.id} 
                  className="overflow-hidden border-yellow-200 transition-colors duration-300"
                  style={{ backgroundColor: 'var(--brand-color-5, #F5FBEF)' }}
                >
                  <CardContent className="p-3 md:p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium truncate max-w-[60%] text-gray-800">
                        {review.customer_name || "Anonymous"}
                      </span>
                      <div className="flex items-center">
                        {renderStars(review.rating)}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-xs md:text-sm text-gray-700 mt-1 md:mt-2 italic line-clamp-3">{review.comment}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
